/**
 * Repeatedly checks a condition at a specified interval until it returns `true`.
 * The function resolves once the condition is met or rejects if the timeout is reached.
 *
 * @param condition A function that returns a boolean. The polling stops when it returns `true`.
 * @param interval The time (in milliseconds) between condition checks. Defaults to 100ms.
 * @param timeout The maximum time (in milliseconds) to wait before rejecting. If not provided, waits indefinitely.
 * @returns A promise that resolves when the condition becomes `true`, or rejects if timeout is reached.
 */
const waitUntil = async (
  condition: () => boolean,
  interval?: number,
  timeout?: number,
): Promise<void> => new Promise((resolve, reject) => {
  const intervalMs = interval ?? 100;

  let timeoutId: NodeJS.Timeout | undefined;

  const check = setInterval(() => {
    if (condition()) {
      clearInterval(check);
      if (timeoutId) clearTimeout(timeoutId);
      resolve();
    }
  }, intervalMs);

  if (timeout) {
    timeoutId = setTimeout(() => {
      clearInterval(check);
      reject(new Error(`waitUntil timed out after ${timeout}ms`));
    }, timeout);
  }
});

export default waitUntil;
